"use client"

import { useEffect, useState } from "react"
import { X } from "lucide-react"
import { fetchAllBadgesWithStatus } from "@/lib/community/community-api"
import type { Badge, BadgeCategory } from "@/lib/community/types"

interface BadgeWallModalProps {
  userId: string
  displayName: string
  onClose: () => void
}

const CATEGORY_LABELS: Record<BadgeCategory, string> = {
  skill: "Skill 系列",
  tool: "Tool 系列",
  workflow: "Workflow 系列",
  agent: "Agent 系列",
  "agent-team": "Agent团队 系列",
  community: "社区贡献",
  milestone: "里程碑",
}

export function BadgeWallModal({ userId, displayName, onClose }: BadgeWallModalProps) {
  const [badges, setBadges] = useState<(Badge & { unlocked: boolean })[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAllBadgesWithStatus(userId).then((data) => {
      setBadges(data)
      setLoading(false)
    })
  }, [userId])

  const unlockedCount = badges.filter((b) => b.unlocked).length
  const totalCount = badges.length
  const progress = totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0

  const groupedBadges = badges.reduce((acc, badge) => {
    if (!acc[badge.category]) acc[badge.category] = []
    acc[badge.category].push(badge)
    return acc
  }, {} as Record<BadgeCategory, Badge[]>)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="liquid-glass-modal rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto m-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-semibold">🏆 {displayName} 的勋章墙</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-foreground/10 rounded-full transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-5 w-5 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-muted-foreground">已获得 {unlockedCount} / {totalCount}</span>
                <span className="font-medium">{progress.toFixed(0)}%</span>
              </div>
              <div className="h-1.5 bg-foreground/8 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {Object.entries(groupedBadges).map(([category, categoryBadges]) => (
              <div key={category} className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-2.5">
                  {CATEGORY_LABELS[category as BadgeCategory]}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {categoryBadges.map((badge) => (
                    <div
                      key={badge.id}
                      className={`liquid-glass-surface p-3 rounded-xl transition-all ${
                        badge.unlockedAt ? "opacity-100" : "opacity-35"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{badge.unlockedAt ? "✅" : "🔒"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{badge.name}</div>
                          {badge.unlockedAt && (
                            <div className="text-xs text-muted-foreground mt-1">
                              获得于 {new Date(badge.unlockedAt).toLocaleDateString("zh-CN")}
                            </div>
                          )}
                          {!badge.unlockedAt && badge.progress && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {badge.progress.current}/{badge.progress.target}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
